/**
 * Visibilidad de consentimiento + región (Fase 0.4 del roadmap de ad-revenue).
 *
 * Objetivo: poder separar un `IPVPS=0` legítimo (el visitante rechazó cookies → no
 * ve anuncios) de un `0` por dato perdido. El SDK NO decide nada: lee dos señales en
 * BRUTO y las muta en el meta de sesión; el SERVIDOR las interpreta (régimen de
 * privacidad por zona horaria + criterio "ve anuncios" por consentimiento). Así la
 * lógica vive donde sí podemos actualizarla al instante, no en este SDK que la caché
 * del publisher congela durante horas (ver Fase 0.1).
 *
 * Muta `meta` por referencia: el transporte reserializa el envelope en CADA flush
 * (queue.ts), así que un "Aceptar" tardío o a mitad de sesión entra solo en el
 * siguiente lote, sin tener que tocar el transporte.
 *
 * A diferencia de startAds, aquí NO hace falta enganche temprano: `impressionViewable`
 * es un evento de una sola vez (se pierde si llegas tarde), pero `addEventListener` del
 * CMP te entrega el estado ACTUAL en cuanto te suscribes y luego avisa de cambios.
 */

import type { ConsentMeta } from "@tracking-web/shared";

/** Lo único que este colector toca del meta (resto de campos: ver index.ts). */
type MetaLike = { tz?: string; cons?: ConsentMeta };

/** Forma mínima del objeto que entrega el CMP (IAB TCF v2.2). */
type TcData = {
  eventStatus?: string; // 'tcloaded' | 'cmpuishown' | 'useractioncomplete'
  gdprApplies?: boolean;
  purpose?: { consents?: Record<string, boolean> };
  vendor?: { consents?: Record<string, boolean> };
};
type TcfApi = (
  cmd: string,
  version: number,
  cb: (data: TcData, success: boolean) => void
) => void;

let started = false;

/** Lee zona horaria (síncrono) y se suscribe al CMP. Idempotente. */
export function startConsent(meta: MetaLike): void {
  if (started) return;
  started = true;

  // 1) Zona horaria IANA, cruda. El servidor la traduce a régimen (mapa server-side).
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) meta.tz = tz;
  } catch {
    /* Intl no disponible: nos quedamos sin tz (el servidor lo trata como "other") */
  }

  // 2) Consentimiento vía TCF. Sin CMP → s:"none" (desconocido; NO asumimos que vio
  //    ni que no vio anuncios). Con CMP, nos suscribimos y reflejamos cada cambio.
  try {
    const w = window as unknown as { __tcfapi?: TcfApi };
    if (typeof w.__tcfapi !== "function") {
      meta.cons = { s: "none" };
      return;
    }
    w.__tcfapi("addEventListener", 2, (data, success) => {
      if (!success || !data) return;
      // 'tcloaded'/'useractioncomplete' = hay decisión utilizable (guardada o recién
      // tomada). 'cmpuishown' = banner visible pero el usuario aún no ha decidido.
      const s =
        data.eventStatus === "useractioncomplete"
          ? "done"
          : data.eventStatus === "cmpuishown"
            ? "ui"
            : "loaded";
      // Finalidades 1..4 como bitmap "0/1". Qué cuenta como "ve anuncios" lo decide
      // el servidor; aquí solo mandamos el dato crudo para poder afinarlo luego.
      const pc = data.purpose?.consents || {};
      let p = "";
      for (let i = 1; i <= 4; i++) p += pc[i] ? "1" : "0";
      meta.cons = {
        g: data.gdprApplies,
        p,
        v: !!data.vendor?.consents?.["755"], // 755 = Google Advertising Products
        s,
      };
    });
  } catch {
    // API de CMP inesperada: degradación silenciosa, lo marcamos como desconocido.
    meta.cons = { s: "none" };
  }
}
